import { CaretLeftIcon, LockSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/data-display/badge";
import { cn } from "@/lib/utils";

const TEMPLATE_PREVIEW_BACK_PATH = "/scheduled-briefings/templates";

const formatTemplatePreviewMetadataLine = (
  updatedLabel: string,
  createdByDisplayName?: string,
): string =>
  createdByDisplayName
    ? `Created by ${createdByDisplayName} • ${updatedLabel}`
    : updatedLabel;

export interface TemplatePreviewHeaderProps {
  title: string;
  description?: string;
  createdByDisplayName?: string;
  /** Pre-formatted date fragment, e.g. `Updated Sep 1 2026`. */
  updatedLabel: string;
  /** Hides the Create briefing action. */
  isArchived?: boolean;
  /** Shows an Archived badge in place of the action when `isArchived`. */
  showArchivedBadge?: boolean;
  onCreateBriefing: () => void;
  className?: string;
}

export const TemplatePreviewHeader = ({
  title,
  description,
  createdByDisplayName,
  updatedLabel,
  isArchived = false,
  showArchivedBadge = false,
  onCreateBriefing,
  className,
}: TemplatePreviewHeaderProps) => (
  <header
    className={cn(
      "shrink-0 border-b border-gray-200 bg-white px-16 pt-4 pb-6",
      className,
    )}
  >
    <Link
      to={TEMPLATE_PREVIEW_BACK_PATH}
      className="inline-flex items-center gap-1 text-xs leading-5 tracking-[0.12px] text-gray-600 no-underline outline-hidden transition-colors hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
    >
      <CaretLeftIcon aria-hidden className="size-3.5" />
      All templates
    </Link>

    <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 max-w-2xl flex-col gap-2">
        <h1 className="text-xl font-medium leading-7 text-gray-900">{title}</h1>
        {description ? (
          <p className="text-sm leading-5 tracking-[0.14px] text-gray-700">
            {description}
          </p>
        ) : null}
        <p className="text-xs leading-5 tracking-[0.12px] text-gray-600">
          {formatTemplatePreviewMetadataLine(
            updatedLabel,
            createdByDisplayName,
          )}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end">
        {isArchived ? (
          showArchivedBadge ? (
            <Badge variant="muted" shape="pill" bordered={false}>
              Archived
            </Badge>
          ) : null
        ) : (
          <Button
            variant="default"
            size="sm"
            className="h-8 gap-2"
            onPress={onCreateBriefing}
          >
            <PlusIcon aria-hidden className="size-4" />
            Create briefing from this template
          </Button>
        )}
        <div className="flex items-center gap-2 text-xs leading-5 tracking-[0.12px] text-gray-600">
          <LockSimpleIcon aria-hidden className="size-3.5" />
          <span>Only owner can edit</span>
        </div>
      </div>
    </div>
  </header>
);
